import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, CloudOff, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useFontStore } from '../../stores/fontStore';

interface ServerEntry {
  fileName: string;
  postscriptName: string;
}

type Section = 'matched' | 'missing-in-manager' | 'missing-on-server';

export function FontServerSync() {
  const fonts = useFontStore((s) => s.fonts);
  const [serverEntries, setServerEntries] = useState<ServerEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<Section, boolean>>({
    matched: false,
    'missing-in-manager': true,
    'missing-on-server': true,
  });

  async function load() {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('cs_server_fonts')
      .select('file_name, postscript_name')
      .order('postscript_name');
    if (!error && data) {
      setServerEntries(data.map((r: any) => ({ fileName: r.file_name, postscriptName: r.postscript_name })));
    }
    setIsLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const { matched, missingInManager, missingOnServer } = useMemo(() => {
    const serverSet = new Set(serverEntries.map((e) => e.postscriptName));
    const managerSet = new Set(fonts.map((f) => f.postscriptName));

    const matched: string[] = [];
    const missingInManager: ServerEntry[] = [];
    const missingOnServer: { postscriptName: string; fileName: string }[] = [];

    for (const e of serverEntries) {
      if (managerSet.has(e.postscriptName)) matched.push(e.postscriptName);
      else missingInManager.push(e);
    }
    for (const f of fonts) {
      if (!serverSet.has(f.postscriptName)) {
        missingOnServer.push({ postscriptName: f.postscriptName, fileName: f.fileName });
      }
    }

    matched.sort();
    missingInManager.sort((a, b) => a.postscriptName.localeCompare(b.postscriptName));
    missingOnServer.sort((a, b) => a.postscriptName.localeCompare(b.postscriptName));

    return { matched, missingInManager, missingOnServer };
  }, [fonts, serverEntries]);

  const toggle = (section: Section) =>
    setExpanded((e) => ({ ...e, [section]: !e[section] }));

  return (
    <div className="border-b border-white/10">
      <div className="flex items-center justify-between px-6 py-3 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white">Server Sync</span>
          <span className="text-xs text-white/50">
            ({matched.length} matched / {missingInManager.length} missing in manager / {missingOnServer.length} missing on server)
          </span>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <SyncSection
        title="Matched"
        count={matched.length}
        icon={<CheckCircle2 size={14} className="text-emerald-400" />}
        expanded={expanded.matched}
        onToggle={() => toggle('matched')}
      >
        {matched.length === 0 ? (
          <EmptyRow text="No matching fonts yet." />
        ) : (
          matched.map((name) => (
            <Row key={name} left={name} />
          ))
        )}
      </SyncSection>

      <SyncSection
        title="Missing in Font Manager"
        count={missingInManager.length}
        icon={<AlertTriangle size={14} className="text-amber-400" />}
        expanded={expanded['missing-in-manager']}
        onToggle={() => toggle('missing-in-manager')}
      >
        {missingInManager.length === 0 ? (
          <EmptyRow text="Every server font is uploaded." />
        ) : (
          missingInManager.map((e) => (
            <Row key={e.postscriptName} left={e.postscriptName} right={e.fileName} />
          ))
        )}
      </SyncSection>

      <SyncSection
        title="Missing on Server"
        count={missingOnServer.length}
        icon={<CloudOff size={14} className="text-rose-400" />}
        expanded={expanded['missing-on-server']}
        onToggle={() => toggle('missing-on-server')}
      >
        {missingOnServer.length === 0 ? (
          <EmptyRow text="Every uploaded font exists on the server." />
        ) : (
          missingOnServer.map((e) => (
            <Row key={e.postscriptName} left={e.postscriptName} right={e.fileName} />
          ))
        )}
      </SyncSection>
    </div>
  );
}

function SyncSection({
  title,
  count,
  icon,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  count: number;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-white/5">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-2 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown size={14} className="text-white/40" />
          ) : (
            <ChevronRight size={14} className="text-white/40" />
          )}
          {icon}
          <span className="text-xs font-medium text-white/80">{title}</span>
        </div>
        <span className="text-xs text-white/40">{count}</span>
      </button>
      {expanded && (
        <div className="px-6 pb-3 max-h-[180px] overflow-y-auto sidebar-scroll">
          {children}
        </div>
      )}
    </div>
  );
}

function Row({ left, right }: { left: string; right?: string }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-xs font-mono">
      <span className="text-white/80">{left}</span>
      {right && <span className="text-white/40">{right}</span>}
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="py-1 text-xs text-white/40 italic">{text}</div>;
}
